<!--
  TeamMembersPanel -- Distributor App, Team & Permissions members roster.

  Card: Team & Permissions: real members table + full kebab
  (https://trello.com/c/IlgSKtZl). PR-1/4.

  Renders the distributor's real member roster sourced from member ACCOUNTS
  (impersonation-aware) via GET /distributor/teamMembers, REPLACING the
  legacy Stripe-subscription roster on this page. No "Price per month"
  billing framing -- this is people, not billing.

  Each row: avatar initials, name (+ "You" chip for the acting user), email,
  a role chip ("Owner" / "Builder") or amber "Pending" chip, scope text,
  last-active text, and a kebab menu. Kebab items vary by role + status:

    WIRED (real handlers):
      - Email <first>      (all rows)            -> mailto:
      - View N stores      (active builder)      -> /distributor/stores
      - Edit name          (active rows)         -> PATCH /accounts/<id>/
      - Reset password     (active rows)         -> POST /auth/sendResetPasswordToken
      - Resend invite      (pending rows)        -> GET /distributor/resendInvitationEmails
      - Copy invite link   (pending rows)        -> navigator.clipboard (/new-user/<token>)
      - Change role to Owner (active builder)    -> POST /distributor/teamMembers/<id>/role
      - Deactivate         (active, non-self)    -> POST /subscription/deleteTeammate/
                           Deactivates the PERSON only; their stores move to
                           the acting Owner and are listed, each linking to
                           the store list's Change owner dialog.
      - Revoke invitation  (pending rows)        -> same route, invite revoked

    DEFERRED (rendered DISABLED with a follow-on-PR hint; never call a
    backend -- the endpoints do not exist yet):
      - Change pre-assigned role    (pending)
      - Transfer ownership          (owner, non-self)

  Design source: planning/design-handoffs/handoff_2026-05-09/
  2026-05-29-distributor-account/design/distributor-sections.jsx
  (function TeamPermissions). Tokens from src/styles/_tokens.css.
  Vuetify icon set is 'md' -- MD ligature names, never "mdi-*".

  ASCII-only. Selectors use data-testid.
-->
<template>
  <div data-testid="team-members-panel" class="tmp-card">
    <div class="tmp-card__header">
      <div>
        <div class="tmp-card__title">Team</div>
        <div data-testid="team-members-summary" class="tmp-card__sub">
          {{ summary }}
        </div>
      </div>
    </div>

    <div
      v-if="notice"
      :data-testid="
        notice.kind === 'error'
          ? 'team-members-notice-error'
          : 'team-members-notice-success'
      "
      class="tmp-notice"
      :class="`tmp-notice--${notice.kind}`"
      role="status"
    >
      {{ notice.message }}
    </div>

    <div class="tmp-rows">
      <div
        v-for="(member, idx) in members"
        :key="member.email"
        :data-testid="`team-member-row-${member.email}`"
        class="tmp-row"
        :class="{ 'tmp-row--pending': member.status === 'pending' }"
      >
        <div
          class="tmp-avatar"
          :class="avatarClass(member)"
          aria-hidden="true"
        >
          {{ initials(member) }}
        </div>

        <div class="tmp-id">
          <div class="tmp-id__name-line">
            <span class="tmp-id__name">{{ member.name }}</span>
            <span
              v-if="member.is_me"
              data-testid="team-member-you-chip"
              class="tmp-chip tmp-chip--you"
            >You</span>
          </div>
          <div class="tmp-id__email">{{ member.email }}</div>
        </div>

        <div class="tmp-meta">
          <div>
            Sees: <span class="tmp-meta__value">{{ member.scope }}</span>
          </div>
          <div v-if="member.status === 'pending'" class="tmp-meta__pending">
            Invited
          </div>
          <div v-else>
            Active:
            <span class="tmp-meta__value">{{ lastActiveLabel(member) }}</span>
          </div>
        </div>

        <span
          data-testid="team-member-chip"
          class="tmp-chip"
          :class="statusChipClass(member)"
        >{{ chipLabel(member) }}</span>

        <div class="tmp-kebab">
          <i
            :data-testid="`team-member-kebab-${idx}`"
            class="material-icons tmp-kebab__trigger"
            :class="{ 'tmp-kebab__trigger--open': openIdx === idx }"
            role="button"
            tabindex="0"
            @click.stop="toggleMenu(idx)"
          >more_horiz</i>

          <div
            v-if="openIdx === idx"
            class="tmp-menu"
            role="menu"
          >
            <!-- Email <first> -- always present, wired -->
            <a
              data-testid="team-kebab-email"
              class="tmp-menu__item"
              :href="`mailto:${member.email}`"
              role="menuitem"
              @click="closeMenu"
            >
              <i class="material-icons tmp-menu__icon">mail_outline</i>
              <span class="tmp-menu__label">
                Email {{ firstName(member) }}
                <span class="tmp-menu__sub">{{ member.email }}</span>
              </span>
            </a>

            <!-- Active builder (not pending, not self) -->
            <template
              v-if="member.status === 'active' && member.role === 'builder'"
            >
              <div
                data-testid="team-kebab-view-stores"
                class="tmp-menu__item"
                role="menuitem"
                @click="onViewStores(member)"
              >
                <i class="material-icons tmp-menu__icon">storefront</i>
                <span class="tmp-menu__label">
                  View {{ member.stores }} stores
                  <span class="tmp-menu__sub">Stores they own or collaborate on</span>
                </span>
              </div>
              <div
                data-testid="team-kebab-edit-member"
                class="tmp-menu__item"
                role="menuitem"
                @click="onEditMember(member)"
              >
                <i class="material-icons tmp-menu__icon">edit</i>
                <span class="tmp-menu__label">Edit team member</span>
              </div>
              <div
                data-testid="team-kebab-reset-password"
                class="tmp-menu__item"
                role="menuitem"
                @click="onResetPassword(member)"
              >
                <i class="material-icons tmp-menu__icon">lock_reset</i>
                <span class="tmp-menu__label">
                  Reset password
                  <span class="tmp-menu__sub">Sends a password-reset link</span>
                </span>
              </div>
              <div class="tmp-menu__divider" />
              <div
                data-testid="team-kebab-change-role"
                class="tmp-menu__item"
                role="menuitem"
                @click="onPromoteToOwner(member)"
              >
                <i class="material-icons tmp-menu__icon">switch_account</i>
                <span class="tmp-menu__label">
                  Change role to Owner
                  <span class="tmp-menu__sub">
                    Grants full team + billing access
                  </span>
                </span>
              </div>
              <div
                data-testid="team-kebab-deactivate"
                class="tmp-menu__item tmp-menu__item--danger"
                role="menuitem"
                @click="onDeactivate(member)"
              >
                <i class="material-icons tmp-menu__icon">block</i>
                <span class="tmp-menu__label">
                  Deactivate
                  <span class="tmp-menu__sub">Their stores move to you</span>
                </span>
              </div>
            </template>

            <!-- Owner row, not the acting user -->
            <template
              v-else-if="member.status === 'active' && member.role === 'owner' && !member.is_me"
            >
              <div
                data-testid="team-kebab-edit-member"
                class="tmp-menu__item"
                role="menuitem"
                @click="onEditMember(member)"
              >
                <i class="material-icons tmp-menu__icon">edit</i>
                <span class="tmp-menu__label">Edit team member</span>
              </div>
              <div
                data-testid="team-kebab-reset-password"
                class="tmp-menu__item"
                role="menuitem"
                @click="onResetPassword(member)"
              >
                <i class="material-icons tmp-menu__icon">lock_reset</i>
                <span class="tmp-menu__label">Reset password</span>
              </div>
              <div class="tmp-menu__divider" />
              <div
                data-testid="team-kebab-transfer"
                class="tmp-menu__item tmp-menu__item--disabled"
                role="menuitem"
                aria-disabled="true"
                :title="DEFERRED_HINT"
              >
                <i class="material-icons tmp-menu__icon">swap_horiz</i>
                <span class="tmp-menu__label">
                  Transfer ownership
                  <span class="tmp-menu__sub">{{ DEFERRED_HINT }}</span>
                </span>
              </div>
              <div
                data-testid="team-kebab-deactivate"
                class="tmp-menu__item tmp-menu__item--danger"
                role="menuitem"
                @click="onDeactivate(member)"
              >
                <i class="material-icons tmp-menu__icon">block</i>
                <span class="tmp-menu__label">Deactivate</span>
              </div>
            </template>

            <!-- Active owner that IS the acting user: edit name only -->
            <template
              v-else-if="member.status === 'active' && member.role === 'owner' && member.is_me"
            >
              <div
                data-testid="team-kebab-edit-member"
                class="tmp-menu__item"
                role="menuitem"
                @click="onEditMember(member)"
              >
                <i class="material-icons tmp-menu__icon">edit</i>
                <span class="tmp-menu__label">Edit team member</span>
              </div>
            </template>

            <!-- Pending invitee -->
            <template v-else-if="member.status === 'pending'">
              <div class="tmp-menu__divider" />
              <div
                data-testid="team-kebab-resend"
                class="tmp-menu__item"
                role="menuitem"
                @click="onResend(member)"
              >
                <i class="material-icons tmp-menu__icon">forward_to_inbox</i>
                <span class="tmp-menu__label">
                  Resend invite
                  <span class="tmp-menu__sub">Re-sends the signup email</span>
                </span>
              </div>
              <!-- Only when the server sent the invite code: it is withheld
                   from callers who are not an Owner/Builder, and without it
                   there is no link to copy. -->
              <div
                v-if="member.reset_code"
                data-testid="team-kebab-copy-link"
                class="tmp-menu__item"
                role="menuitem"
                @click="onCopyLink(member)"
              >
                <i class="material-icons tmp-menu__icon">content_copy</i>
                <span class="tmp-menu__label">Copy invite link</span>
              </div>
              <div
                data-testid="team-kebab-change-role"
                class="tmp-menu__item tmp-menu__item--disabled"
                role="menuitem"
                aria-disabled="true"
                :title="DEFERRED_HINT"
              >
                <i class="material-icons tmp-menu__icon">switch_account</i>
                <span class="tmp-menu__label">
                  Change pre-assigned role
                  <span class="tmp-menu__sub">{{ DEFERRED_HINT }}</span>
                </span>
              </div>
              <div class="tmp-menu__divider" />
              <div
                data-testid="team-kebab-revoke"
                class="tmp-menu__item tmp-menu__item--danger"
                role="menuitem"
                @click="onDeactivate(member)"
              >
                <i class="material-icons tmp-menu__icon">cancel</i>
                <span class="tmp-menu__label">Revoke invitation</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>

    <div class="tmp-card__footer">
      Pre-create + email invite. They set their own password on first sign-in.
    </div>

    <!--
      Promote-to-Owner confirmation. Promoting to Owner is a privilege grant
      (full team + billing access), so the design confirms intent before the
      POST. Matches the v-dialog pattern of HandOffOwnershipDialog /
      EditPaymentInfoDialog.
    -->
    <v-dialog v-model="promoteDialog.open" persistent max-width="480px">
      <v-card data-testid="team-promote-dialog">
        <v-card-title>
          <div class="d-flex align-center">
            <h1>Change role to Owner</h1>
            <v-spacer />
            <v-btn icon variant="plain" @click="cancelPromote">
              <v-icon icon="close" />
            </v-btn>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div class="text-h6 mb-2">
            Make
            <strong>{{ promoteDialog.member && promoteDialog.member.name }}</strong>
            an Owner?
          </div>
          <div>
            Owners get full team + billing access -- they can invite and
            remove teammates, change roles, and manage payment settings for
            the whole distributor. This cannot be undone from here.
          </div>
        </v-card-text>
        <v-card-actions class="py-3 px-3">
          <v-btn
            variant="flat"
            data-testid="team-promote-cancel"
            @click="cancelPromote"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="primary"
            class="text-white"
            data-testid="team-promote-confirm"
            :loading="promoteDialog.saving"
            @click="confirmPromote"
          >
            Make Owner
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
      Deactivate / revoke dialog. Confirm names the person; after a
      deactivation it becomes the results view: the stores that moved to the
      acting Owner, each linking to the store list's Change owner dialog. A
      revoked invite has no stores, so it simply closes.
    -->
    <v-dialog v-model="deactivateDialog.open" persistent max-width="520px">
      <v-card data-testid="team-deactivate-dialog">
        <v-card-title>
          <div class="d-flex align-center">
            <h1>{{ deactivateTitle }}</h1>
            <v-spacer />
            <v-btn icon variant="plain" @click="closeDeactivate">
              <v-icon icon="close" />
            </v-btn>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text v-if="!deactivateDialog.results">
          <div
            v-if="deactivateDialog.error"
            data-testid="team-deactivate-error"
            class="tmp-notice tmp-notice--error tmp-dialog-error"
            role="alert"
          >
            {{ deactivateDialog.error }}
          </div>
          <template v-if="deactivateIsInvite">
            <div class="text-h6 mb-2">
              Revoke the invitation for
              <strong>{{ deactivateDialog.member && deactivateDialog.member.email }}</strong>?
            </div>
            <div>Their invite link will stop working.</div>
          </template>
          <template v-else>
            <div class="text-h6 mb-2">
              Deactivate
              <strong>{{ deactivateDialog.member && deactivateDialog.member.name }}</strong>?
            </div>
            <div v-if="actorInTenant">
              They will no longer be able to sign in. Any stores they own will
              be reassigned to you, and you can move each one to someone else.
            </div>
            <div v-else>
              They will no longer be able to sign in. Any stores they own will
              be reassigned to an Owner, who can move each one to someone else.
            </div>
          </template>
        </v-card-text>
        <v-card-text v-else data-testid="team-deactivate-results">
          <div class="mb-2">
            {{ deactivateDialog.member && deactivateDialog.member.name }} was
            deactivated.
          </div>
          <div
            v-if="!deactivateDialog.results.stores.length"
            data-testid="team-deactivate-no-stores"
          >
            No stores to reassign.
          </div>
          <template v-else>
            <div class="mb-2">
              These stores now belong to
              <strong>{{ deactivateDialog.results.to }}</strong>:
            </div>
            <div
              v-for="store in deactivateDialog.results.stores"
              :key="store.id"
              :data-testid="`team-deactivate-store-${store.id}`"
              class="tmp-reassigned"
            >
              <span class="tmp-reassigned__name">{{ store.name }}</span>
              <v-btn
                variant="text"
                size="small"
                color="primary"
                :data-testid="`team-deactivate-change-owner-${store.id}`"
                @click="onChangeStoreOwner(store)"
              >
                Change owner
              </v-btn>
            </div>
          </template>
        </v-card-text>
        <v-card-actions class="py-3 px-3">
          <template v-if="!deactivateDialog.results">
            <v-btn
              variant="flat"
              data-testid="team-deactivate-cancel"
              @click="closeDeactivate"
            >
              Cancel
            </v-btn>
            <v-spacer />
            <v-btn
              variant="flat"
              color="error"
              class="text-white"
              data-testid="team-deactivate-confirm"
              :loading="deactivateDialog.saving"
              @click="confirmDeactivate"
            >
              {{ deactivateIsInvite ? "Revoke" : "Deactivate" }}
            </v-btn>
          </template>
          <template v-else>
            <v-spacer />
            <v-btn
              variant="flat"
              color="primary"
              class="text-white"
              data-testid="team-deactivate-results-close"
              @click="closeDeactivate"
            >
              Done
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!--
      Edit-team-member dialog -- edits first / last name, EMAIL, and role in
      ONE coherent save. Email is the account's LOGIN identity, so this is not
      a cosmetic name edit: the single Save POSTs
      /distributor/teamMembers/<id>/profile, which revalidates the email,
      syncs the username so login stays coherent, applies the role change, and
      re-sends any still-open invitation to the corrected address (all
      server-side). A duplicate-email 422 is surfaced inline; the dialog stays
      open. Matches the account dialogs' v-dialog style.
    -->
    <v-dialog v-model="editMemberDialog.open" persistent max-width="480px">
      <v-card data-testid="team-edit-member-dialog">
        <v-card-title>
          <div class="d-flex align-center">
            <h1>Edit team member</h1>
            <v-spacer />
            <v-btn icon variant="plain" @click="cancelEditMember">
              <v-icon icon="close" />
            </v-btn>
          </div>
        </v-card-title>
        <v-divider />
        <v-card-text>
          <div
            v-if="editMemberDialog.error && !editMemberEmailError"
            data-testid="team-edit-member-error"
            class="tmp-notice tmp-notice--error tmp-dialog-error"
            role="alert"
          >
            {{ editMemberDialog.error }}
          </div>
          <v-form @submit.prevent>
            <v-text-field
              v-model="editMemberDialog.firstName"
              label="First name"
              data-testid="team-edit-member-first"
            />
            <v-text-field
              v-model="editMemberDialog.lastName"
              label="Last name"
              data-testid="team-edit-member-last"
            />
            <v-text-field
              v-model="editMemberDialog.email"
              label="Email"
              type="email"
              data-testid="team-edit-member-email"
              :error="Boolean(editMemberEmailError)"
              :error-messages="editMemberEmailError || []"
            />
            <div
              v-if="editMemberEmailError"
              data-testid="team-edit-member-email-error"
              class="tmp-field-error"
              role="alert"
            >
              {{ editMemberEmailError }}
            </div>
            <div class="tmp-dialog-hint">
              This is their login identity -- they'll sign in with this email.
            </div>
            <v-select
              v-model="editMemberDialog.role"
              :items="assignableRoles"
              label="Role"
              data-testid="team-edit-member-role"
            />
          </v-form>
        </v-card-text>
        <v-card-actions class="py-3 px-3">
          <v-btn
            variant="flat"
            data-testid="team-edit-member-cancel"
            @click="cancelEditMember"
          >
            Cancel
          </v-btn>
          <v-spacer />
          <v-btn
            variant="flat"
            color="primary"
            class="text-white"
            data-testid="team-edit-member-save"
            :disabled="!canSaveEditMember"
            :loading="editMemberDialog.saving"
            @click="confirmEditMember"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { inject } from "vue";
import { useTeamMembers } from "@/composables/useTeamMembers";

const DEFERRED_HINT = "Ships in a follow-on PR";

// Assignable distributor-team roles for the Edit Team Member combobox. Keyed
// by SLUG (role ids drift across DBs -- key by slug, never id), matching the
// role-change endpoint's allowlist (owner / builder). ``sales_executive`` is
// presented as "builder" by the roster, so the two slugs here cover every
// row the dialog can open. Do NOT add client-side or superadmin roles.
const ASSIGNABLE_ROLES = [
  { title: "Owner", value: "owner" },
  { title: "Builder", value: "builder" },
];

// Client-side email format gate so Save is disabled until the address is
// plausibly valid (the server revalidates uniqueness). Mirrors the shape the
// login path uses to decide "this looks like an email".
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

export default {
  name: "TeamMembersPanel",

  props: {
    // Members may be passed in (mount-testable) or loaded via the composable
    // when the parent does not supply them.
    members: {
      type: Array,
      default: () => [],
    },
  },

  // The parent owns the roster it passes in, so it reloads on this.
  emits: ["members-changed"],

  setup() {
    // Inject axios so the panel is mount-testable without a live backend.
    // Falls back to the shared instance for production use.
    let axios = inject("axios", null);
    if (!axios) {
      // Lazy import avoids a hard dependency in the test env where axios is
      // always provided.
      // eslint-disable-next-line global-require
      axios = require("@/axios").default;
    }
    const team = useTeamMembers({ axios });
    return { team, DEFERRED_HINT };
  },

  data() {
    return {
      // Index of the row whose kebab menu is open (null = none).
      openIdx: null,
      // Promote-to-Owner confirmation dialog state.
      promoteDialog: {
        open: false,
        member: null,
        saving: false,
      },
      // Deactivate / revoke dialog. ``results`` is null while confirming and
      // ``{ stores, to }`` once a deactivation has reassigned stores.
      deactivateDialog: {
        open: false,
        member: null,
        saving: false,
        error: null,
        results: null,
      },
      // Edit-team-member dialog state (first / last / email / role). Email is
      // the login identity, so a change flows through the coherent
      // profile-edit endpoint. ``error`` holds a server 422 detail surfaced
      // inline; the dialog stays open until Save succeeds or Cancel.
      editMemberDialog: {
        open: false,
        member: null,
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        saving: false,
        error: null,
      },
    };
  },

  computed: {
    assignableRoles() {
      return ASSIGNABLE_ROLES;
    },
    // The acting user is one of the rows unless a Super Admin is
    // impersonating; only then do the stores go to an Owner rather than
    // to "you" (the server's _store_reassignment_recipient).
    actorInTenant() {
      return (this.members || []).some((m) => m.is_me);
    },
    deactivateIsInvite() {
      const m = this.deactivateDialog.member;
      return Boolean(m && m.status === "pending");
    },
    deactivateTitle() {
      if (this.deactivateDialog.results) return "Teammate deactivated";
      return this.deactivateIsInvite ? "Revoke invitation" : "Deactivate teammate";
    },
    canSaveEditMember() {
      // Save is enabled only once (a) both names are non-empty and (b) the
      // email is a plausibly-valid address (the server revalidates
      // uniqueness). Blanking a name must not persist an empty string. Never
      // gate on the role -- it is always preselected to the current role.
      const first = (this.editMemberDialog.firstName || "").trim();
      const last = (this.editMemberDialog.lastName || "").trim();
      const email = (this.editMemberDialog.email || "").trim();
      return Boolean(first) && Boolean(last) && EMAIL_RE.test(email);
    },
    summary() {
      const list = this.members || [];
      const owners = list.filter((m) => m.role === "owner").length;
      const builders = list.filter((m) => m.role === "builder").length;
      const ownerLabel = owners === 1 ? "1 owner" : `${owners} owners`;
      const builderLabel =
        builders === 1 ? "1 builder" : `${builders} builders`;
      return `${list.length} people · ${ownerLabel} · ${builderLabel}`;
    },
    notice() {
      return this.team.notice.value;
    },
    // A duplicate-email 422 is about the email field specifically, so surface
    // it inline at the field (visually flagged) rather than as a generic
    // dialog-top alert. Detect it by the server phrase "email address" so the
    // field-level display fires for the backend's duplicate-email message.
    editMemberEmailError() {
      const msg = this.editMemberDialog.error;
      if (msg && /email address/i.test(msg)) {
        return msg;
      }
      return null;
    },
  },

  methods: {
    toggleMenu(idx) {
      this.openIdx = this.openIdx === idx ? null : idx;
    },
    closeMenu() {
      this.openIdx = null;
    },
    initials(member) {
      const name = (member.name || member.email || "").trim();
      const parts = name.split(/\s+/).filter(Boolean);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      if (parts.length === 1 && parts[0].length) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return "?";
    },
    firstName(member) {
      const name = (member.name || "").trim();
      return name ? name.split(/\s+/)[0] : member.email;
    },
    avatarClass(member) {
      if (member.status === "pending") return "tmp-avatar--pending";
      return member.role === "owner"
        ? "tmp-avatar--owner"
        : "tmp-avatar--builder";
    },
    chipLabel(member) {
      if (member.status === "pending") return "Pending";
      return member.role === "owner" ? "Owner" : "Builder";
    },
    statusChipClass(member) {
      if (member.status === "pending") return "tmp-chip--pending";
      return member.role === "owner"
        ? "tmp-chip--owner"
        : "tmp-chip--builder";
    },
    lastActiveLabel(member) {
      if (!member.last_active) return "None yet";
      // Render a simple, locale-aware date; relative formatting can land
      // later. The raw ISO is parseable in jsdom.
      const d = new Date(member.last_active);
      if (Number.isNaN(d.getTime())) return "None yet";
      return d.toLocaleDateString();
    },

    // --- WIRED kebab handlers ---------------------------------------------
    async onResetPassword(member) {
      this.closeMenu();
      await this.team.resetPassword(member);
    },
    async onResend() {
      this.closeMenu();
      await this.team.resendInvite();
    },
    async onCopyLink(member) {
      this.closeMenu();
      await this.team.copyInviteLink(member);
    },
    onViewStores() {
      this.closeMenu();
      // No per-builder filter route exists yet; navigate to the stores list.
      this.$router.push("/distributor/stores");
    },
    // --- Promote to Owner (active builder) --------------------------------
    onPromoteToOwner(member) {
      this.closeMenu();
      this.promoteDialog.member = member;
      this.promoteDialog.open = true;
    },
    cancelPromote() {
      this.promoteDialog.open = false;
      this.promoteDialog.member = null;
    },
    async confirmPromote() {
      const member = this.promoteDialog.member;
      if (!member) {
        this.promoteDialog.open = false;
        return;
      }
      this.promoteDialog.saving = true;
      try {
        // changeRole refreshes the roster on success so the chip + kebab
        // item set for the row update without a manual reload here.
        await this.team.changeRole(member, "owner");
      } finally {
        this.promoteDialog.saving = false;
        this.promoteDialog.open = false;
        this.promoteDialog.member = null;
      }
    },

    // --- Deactivate (active) / Revoke (pending) -----------------------------
    onDeactivate(member) {
      this.closeMenu();
      this.deactivateDialog.member = member;
      this.deactivateDialog.error = null;
      this.deactivateDialog.results = null;
      this.deactivateDialog.open = true;
    },
    closeDeactivate() {
      this.deactivateDialog.open = false;
      this.deactivateDialog.member = null;
      this.deactivateDialog.error = null;
      this.deactivateDialog.results = null;
    },
    async confirmDeactivate() {
      const member = this.deactivateDialog.member;
      if (!member) {
        this.closeDeactivate();
        return;
      }
      this.deactivateDialog.saving = true;
      this.deactivateDialog.error = null;
      try {
        const result = await this.team.deactivateMember(member);
        if (!result.ok) {
          this.deactivateDialog.error = result.message;
          return;
        }
        this.$emit("members-changed");
        if (this.deactivateIsInvite) {
          this.closeDeactivate();
          return;
        }
        this.deactivateDialog.results = {
          stores: result.reassignedStores,
          to: result.reassignedTo ? result.reassignedTo.name : "you",
        };
      } finally {
        this.deactivateDialog.saving = false;
      }
    },
    onChangeStoreOwner(store) {
      this.closeDeactivate();
      this.$router.push({
        path: "/distributor/stores",
        query: { changeOwner: String(store.id) },
      });
    },

    // --- Edit team member (active rows) -----------------------------------
    // Opens the modal seeded with the member's current first / last / email /
    // role. Email is the login identity, so Save flows through the coherent
    // profile-edit endpoint (see useTeamMembers.editMember).
    onEditMember(member) {
      this.closeMenu();
      const parts = (member.name || "").trim().split(/\s+/).filter(Boolean);
      this.editMemberDialog.member = member;
      this.editMemberDialog.firstName = parts[0] || "";
      this.editMemberDialog.lastName = parts.slice(1).join(" ") || "";
      this.editMemberDialog.email = member.email || "";
      // Preselect the member's current role slug (owner / builder).
      this.editMemberDialog.role = member.role || "builder";
      this.editMemberDialog.error = null;
      this.editMemberDialog.open = true;
    },
    cancelEditMember() {
      this.editMemberDialog.open = false;
      this.editMemberDialog.member = null;
      this.editMemberDialog.error = null;
    },
    async confirmEditMember() {
      const member = this.editMemberDialog.member;
      if (!member) {
        this.editMemberDialog.open = false;
        return;
      }
      // Client-side gate mirrors the Save button's disabled state --
      // defensive against a programmatic invoke.
      if (!this.canSaveEditMember) {
        this.editMemberDialog.error =
          "Enter a first name, last name, and a valid email address.";
        return;
      }
      this.editMemberDialog.saving = true;
      this.editMemberDialog.error = null;
      try {
        const result = await this.team.editMember(member, {
          firstName: this.editMemberDialog.firstName,
          lastName: this.editMemberDialog.lastName,
          email: (this.editMemberDialog.email || "").trim(),
          // Only send a role change when it differs from the current role.
          role:
            this.editMemberDialog.role !== member.role
              ? this.editMemberDialog.role
              : undefined,
        });
        if (result && result.ok) {
          this.editMemberDialog.open = false;
          this.editMemberDialog.member = null;
        } else {
          // Surface the server's inline detail (e.g. duplicate-email 422)
          // and keep the dialog open so the user can correct the address.
          this.editMemberDialog.error =
            (result && result.message) || "Could not update the team member.";
        }
      } finally {
        this.editMemberDialog.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.tmp-card {
  background: var(--tf-surface, #fff);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 12px);
  max-width: 820px;
}
.tmp-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--tf-sp-4, 16px) var(--tf-sp-5, 20px);
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.tmp-card__title {
  font-size: var(--tf-size-h4, 1.125rem);
  font-weight: 600;
  color: var(--tf-fg-1, #212121);
}
.tmp-card__sub {
  font-size: var(--tf-size-caption, 0.8125rem);
  color: var(--tf-fg-4, #757575);
  margin-top: 2px;
}
.tmp-card__footer {
  padding: var(--tf-sp-3, 12px) var(--tf-sp-5, 20px);
  font-size: var(--tf-size-caption, 0.75rem);
  color: var(--tf-fg-4, #757575);
  border-top: 1px solid var(--tf-border, #e0e0e0);
}
.tmp-notice {
  margin: var(--tf-sp-3, 12px) var(--tf-sp-5, 20px) 0;
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  border-radius: var(--tf-radius-xs, 4px);
  font-size: var(--tf-size-caption, 0.8125rem);
}
.tmp-notice--success {
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary-press, #0a78b8);
}
.tmp-reassigned {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--tf-sp-1, 4px) 0;
  border-bottom: 1px solid var(--tf-border, #e0e0e0);
}
.tmp-reassigned__name {
  color: var(--tf-fg-1, #212121);
}
.tmp-notice--error {
  background: #fdecec;
  color: var(--tf-error, #d32f2f);
}
.tmp-rows {
  display: flex;
  flex-direction: column;
  gap: var(--tf-sp-2, 6px);
  padding: var(--tf-sp-4, 16px) var(--tf-sp-5, 20px);
}
.tmp-row {
  display: flex;
  align-items: center;
  gap: var(--tf-sp-4, 14px);
  padding: var(--tf-sp-3, 12px) var(--tf-sp-4, 14px);
  background: var(--tf-surface-alt, #fafbfc);
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: var(--tf-radius-md, 10px);
}
.tmp-row--pending {
  opacity: 0.85;
}
.tmp-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 500;
  font-size: 13px;
  flex-shrink: 0;
}
.tmp-avatar--owner {
  background: var(--tf-secondary, #133752);
  color: #fff;
}
.tmp-avatar--builder {
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary, #119ae9);
}
.tmp-avatar--pending {
  background: #eef0f3;
  color: var(--tf-fg-4, #757575);
}
.tmp-id {
  flex: 1;
  min-width: 0;
}
.tmp-id__name-line {
  display: flex;
  align-items: center;
  gap: 8px;
}
.tmp-id__name {
  font-size: 14px;
  font-weight: 500;
  color: var(--tf-fg-1, #212121);
}
.tmp-id__email {
  font-size: 12px;
  color: var(--tf-fg-4, #757575);
  margin-top: 2px;
}
.tmp-meta {
  font-size: 11px;
  color: var(--tf-fg-4, #757575);
  line-height: 1.5;
  text-align: right;
  min-width: 160px;
}
.tmp-meta__value {
  color: var(--tf-fg-2, #424242);
}
.tmp-meta__pending {
  color: #8a6d3b;
}
.tmp-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 9999px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.tmp-chip--owner {
  background: var(--tf-secondary, #133752);
  color: #fff;
}
.tmp-chip--builder {
  background: var(--tf-primary-soft, #e3f2fd);
  color: var(--tf-primary, #119ae9);
}
.tmp-chip--pending {
  background: #fff8e1;
  color: #8a6d3b;
}
.tmp-chip--you {
  background: #eef0f3;
  color: var(--tf-fg-4, #757575);
  font-size: 9px;
  padding: 1px 7px;
}
.tmp-kebab {
  position: relative;
}
.tmp-kebab__trigger {
  font-size: 18px;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  color: var(--tf-fg-4, #757575);
}
.tmp-kebab__trigger--open {
  color: var(--tf-primary, #119ae9);
  background: var(--tf-primary-soft, #e3f2fd);
}
.tmp-menu {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  z-index: 5;
  background: #fff;
  border: 1px solid var(--tf-border, #e0e0e0);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  min-width: 240px;
  padding: 6px 0;
}
.tmp-menu__item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 9px 12px;
  cursor: pointer;
  color: var(--tf-fg-2, #424242);
  text-decoration: none;
}
.tmp-menu__item:hover {
  background: var(--tf-primary-soft, #e3f2fd);
}
.tmp-menu__item--disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.tmp-menu__item--disabled:hover {
  background: transparent;
}
.tmp-menu__item--danger {
  color: var(--tf-error, #d32f2f);
}
.tmp-menu__icon {
  font-size: 17px;
  color: var(--tf-fg-4, #757575);
  margin-top: 1px;
  flex-shrink: 0;
}
.tmp-menu__label {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  color: var(--tf-fg-1, #212121);
  line-height: 1.35;
}
.tmp-menu__sub {
  display: block;
  font-size: 11px;
  color: var(--tf-fg-4, #757575);
  margin-top: 2px;
  line-height: 1.4;
}
.tmp-menu__divider {
  height: 1px;
  background: var(--tf-border, #e0e0e0);
  margin: 4px 0;
}
.tmp-dialog-error {
  margin: 0 0 var(--tf-sp-3, 12px);
}
.tmp-dialog-hint {
  font-size: var(--tf-size-caption, 0.75rem);
  color: var(--tf-fg-4, #757575);
  margin: -8px 0 12px;
}
.tmp-field-error {
  font-size: var(--tf-size-caption, 0.75rem);
  color: var(--tf-error, #d32f2f);
  margin: -8px 0 12px;
}
</style>
